import ComponentsManager from "../ComponentsManager.js";
import CenterMenu from "./CenterMenu.js";
import ApiService from "../serverConnection/ApiService.js";
import OverlayManager from "../OverlayManager.js";
import UserProfile from "../serverConnection/UserProfile.js";
import ProfileView from "./centerMenuTab/profileView.js";
import LanguageManager from "../LanguageManager.js";

/**
 * @author Corentin (ccharton) Charton
 * Edited by: Sonam (scrumier) Crumiere
 * @description This is a public deployment: registration, password login, OTP, 42 OAuth,
 * Google OAuth and friends have all been removed server-side. Guest is the only way in,
 * but the player picks a display name before entering: that name is what the opponent
 * sees during a match, so it has to reach the server (it lands on the ephemeral guest
 * record, not on an account).
 */
class LoginMenu {
    #logButtonClicked = false
    #displayNameStorageKey = 'ft_transcendence_display_name'
    #stringSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    #maxNameGraphemes = 12
    // Mirrors UserProfileSerializer.DISPLAY_NAME_ALLOWED_CHARS on the (now removed)
    // backend: printable Unicode (U+0020-U+D7FF, U+E000-U+10FFFF), no surrogates.
    // Built from code points (rather than a literal regex) to avoid any ambiguity
    // between the "퟿" escape and an actual U+D7FF character in this source file.
    #displayNameAllowedChars = new RegExp(
        '^[' + String.fromCodePoint(0x0020) + '-' + String.fromCodePoint(0xD7FF) +
        String.fromCodePoint(0xE000) + '-' + String.fromCodePoint(0x10FFFF) + ']+$',
        'u'
    )
    #onEnterKeyBind = this.#onEnterKey.bind(this)
    #graphemeInputListenerBind = this.#graphemeInputListener.bind(this)

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Prefill the name field from a previous visit and wire the Enter key
     * plus live grapheme truncation. Called once the login menu component is in the DOM.
     */
    hydrate() {
        const input = document.getElementById('displayNameBox')
        if (!input) {
            return
        }

        const savedName = localStorage.getItem(this.#displayNameStorageKey)
        if (savedName) {
            input.value = savedName
        }

        input.removeEventListener('keydown', this.#onEnterKeyBind)
        input.addEventListener('keydown', this.#onEnterKeyBind)
        input.removeEventListener('input', this.#graphemeInputListenerBind)
        input.addEventListener('input', this.#graphemeInputListenerBind)

        this.#hideNameError()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Submit the name on Enter, same as clicking the validate button
     * @param event {KeyboardEvent}
     */
    #onEnterKey(event) {
        if (event.key === 'Enter') {
            event.preventDefault()
            this.chooseName()
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Live-truncate the name field to the max grapheme count while typing,
     * same approach as profileView.js's displayName editor.
     * @param event {InputEvent}
     */
    #graphemeInputListener(event) {
        const graphemes = Array.from(this.#stringSegmenter.segment(event.target.value), s => s.segment)
        if (graphemes.length > this.#maxNameGraphemes) {
            event.target.value = graphemes.slice(0, this.#maxNameGraphemes).join('')
        }
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Validate a candidate display name against the same rule the server
     * enforces (UserProfileSerializer.validate_displayName): non-empty after trimming,
     * printable Unicode only, at most 12 graphemes.
     * @param rawValue {string}
     * @returns {string|null} - An error code to show, or null if the name is valid
     */
    #validateDisplayName(rawValue) {
        if (typeof rawValue !== 'string') {
            return 'PROFILE_DISPLAY_NAME_INVALID'
        }
        const trimmed = rawValue.trim()
        if (!trimmed) {
            return 'PROFILE_DISPLAY_NAME_INVALID'
        }
        if (!this.#displayNameAllowedChars.test(trimmed)) {
            return 'PROFILE_DISPLAY_NAME_INVALID'
        }
        const graphemeCount = Array.from(this.#stringSegmenter.segment(trimmed)).length
        if (graphemeCount > this.#maxNameGraphemes) {
            return 'PROFILE_DISPLAY_NAME_TOO_LONG'
        }
        return null
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Show an inline error under the name field
     * @param errorCode {string}
     */
    #showNameError(errorCode) {
        const errorEl = document.getElementById('loginNameError')
        if (!errorEl) {
            return
        }
        const knownCodes = new Set(['PROFILE_DISPLAY_NAME_INVALID', 'PROFILE_DISPLAY_NAME_TOO_LONG'])
        errorEl.dataset.i18n = knownCodes.has(errorCode) ? errorCode : 'PROFILE_DISPLAY_NAME_INVALID'
        errorEl.classList.remove('hidden')
        LanguageManager.updateContent()
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Hide the inline name error
     */
    #hideNameError() {
        document.getElementById('loginNameError')?.classList.add('hidden')
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Finalize the login process: create a fresh guest account then push
     * the chosen display name onto it
     * @param displayName {string} - The validated, trimmed display name
     */
    async finalizeLogin(displayName) {
        const userAlreadyConnected = await this.#checkUserAlreadyConnectedAndReset()

        try {
            await ApiService.loginGuest()
            const profile = await ApiService.updateProfile({ displayName })

            UserProfile.setProfile(profile)
            UserProfile.setLoggedIn(true)
            localStorage.setItem(this.#displayNameStorageKey, profile.displayName || displayName)

            this.#logButtonClicked = false

            if (!userAlreadyConnected) {
                ComponentsManager.hideComponent('#loginMenu');
                await this.#loadGuestCenterMenu()
            }
        } catch (e) {
            this.#logButtonClicked = false
            this.#showNameError(e?.message)
            throw e
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Restore an already-authenticated guest session on page load (the guest's
     * refresh cookie is still valid), skipping the login menu and the guest account creation
     * call entirely.
     */
    async restoreGuestSession() {
        try {
            const profile = await ApiService.getProfile(true)
            UserProfile.setProfile(profile)
            UserProfile.setLoggedIn(true)
            if (profile?.displayName) {
                localStorage.setItem(this.#displayNameStorageKey, profile.displayName)
            }

            await this.#loadGuestCenterMenu()
        } catch (e) {
            console.error('Unable to restore guest session:', e)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Load the center menu on the profile tab and apply the guest-only UI setup
     */
    async #loadGuestCenterMenu() {
        await ComponentsManager.loadComponent(
            'centerMenu',
            '#centerMenuContainer',
            './overlay/components/centerMenu.html',
            './overlay/styles/centerMenu.css'
        )

        const tabPicker = document.getElementById('tabPicker');
        if (tabPicker) {
            tabPicker.addEventListener('wheel', OverlayManager.wheelScrollToYBind)
        }

        const profileTabBtn = document.querySelector('#profileTabBtn')
        CenterMenu.pickerBtnActive(profileTabBtn)
        await ComponentsManager.loadComponent('profileTabCenterMenu', '#centerMenuTabContainer', './overlay/components/centerMenuTab/profileView.html', './overlay/styles/centerMenuTab/profileView.css')

        this.#setupGuestMode()
        this.#setupProfileTabForGuest()
    }

    async #checkUserAlreadyConnectedAndReset() {
        if (UserProfile.isLoggedIn()) {
            UserProfile.clearProfile()
            await OverlayManager.reset()
            return true
        }
        return false
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * @description Validate the chosen name, then log the user in as a guest carrying it
     */
    async chooseName() {
        if (this.#logButtonClicked || UserProfile.isLoggedIn()) {
            return
        }

        const input = document.getElementById('displayNameBox')
        const rawValue = input ? input.value : ''
        const errorCode = this.#validateDisplayName(rawValue)
        if (errorCode) {
            this.#showNameError(errorCode)
            return
        }
        this.#hideNameError()

        this.#logButtonClicked = true

        try {
            await this.finalizeLogin(rawValue.trim())
        } catch (error) {
            console.error('Unable to login as guest:', error)
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Setup the center menu for guest mode. Stats and match history are
     * now computed and stored client-side (LocalStats), so unlike the original guest
     * mode they work fine without a real account and stay visible.
     */
    #setupGuestMode() {
        const visibleTabButtonIds = new Set(['statTabBtn', 'matchTabBtn', 'profileTabBtn'])
        const pickerBtnArray = document.querySelectorAll('.pickerBtn')
        pickerBtnArray.forEach(button => {
            if (!visibleTabButtonIds.has(button.id)) {
                button.classList.add('hidden')
                button.disabled = true
            }
        })

        const statusBtnArray = document.querySelectorAll('.statusBtn')
        statusBtnArray.forEach(button => {
            if (button.dataset.action !== 'showProfileView' && button.dataset.action !== 'disconnectUser') {
                button.classList.add('hidden')
                button.disabled = true
            }
        })

        const horizontalSeparatorArray = document.querySelectorAll('.horizontalSeparator')
        horizontalSeparatorArray.forEach(separator => {
            separator.classList.add('hidden')
        })

        const profileOnlineIndicator = document.getElementById('profileOnlineIndicator')
        if (profileOnlineIndicator) {
            profileOnlineIndicator.classList.add('hidden')
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Setup the profile tab for guest mode
     */
    #setupProfileTabForGuest() {
        CenterMenu.hydrate()
        ProfileView.hydrate()

        const profileTabPictureContainer = document.getElementById('profileTabPictureContainer')
        if (profileTabPictureContainer) {
            profileTabPictureContainer.style.margin = '1% 1% 1% 5%'
        }

        const profileMenuCard = document.getElementById('profileMenuCard')
        if (profileMenuCard) {
            profileMenuCard.style.height = '92%'
            profileMenuCard.style.width = '85%'
        }
    }
}

export default new LoginMenu()
